import { computed, toValue } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'

import { resolveNotificationResourceRoute } from '@/composables/useNotificationResourceRoutes'
import {
    NOTIFICATION_USER_LIST_QUERY_KEY,
    useQueryNotificationUserList,
} from '@/queries/notification/useQueryNotificationUserList'
import { useQueryNotificationUserMarkRead } from '@/queries/notification/useQueryNotificationUserMarkRead'
import { useQueryNotificationUserMarkUnread } from '@/queries/notification/useQueryNotificationUserMarkUnread'
import { useQueryNotificationUserDelete } from '@/queries/notification/useQueryNotificationUserDelete'

/**
 * useUserNotifications — the signed-in user's undeleted notifications (read and unread), newest first.
 *
 * Pass `resourceId` to scope every read on this instance (`notifications` and the counts) and to
 * default every write to that resource, so a caller showing one watch's history never re-filters the
 * list itself. `resourceType` narrows reads to one kind of resource (e.g. every watch notification)
 * for callers that badge a feature rather than a single record.
 *
 * `notificationsFor(id)` / `unreadCountFor(id)` read one resource out of the instance scope, for callers
 * that badge several records (e.g. a report's linked watches) from a single list.
 *
 * `markRead` / `markUnread` / `remove` take `{ id }` (one row) or `{ resourceId }` (one resource) and
 * fall back to the instance scope (all when unscoped). Each writes to the cached list immediately and
 * reverts it if the request fails.
 *
 * @param {{
 *   resourceId?: import('vue').MaybeRefOrGetter<string|null>,
 *   resourceType?: import('vue').MaybeRefOrGetter<string|null>,
 * }} [options]
 */
export function useUserNotifications({ resourceId = null, resourceType = null } = {}) {
    const queryClient = useQueryClient()
    const { data, isFetching, isError, error, refetch } = useQueryNotificationUserList()
    const { mutateAsync: markReadRequest } = useQueryNotificationUserMarkRead()
    const { mutateAsync: markUnreadRequest } = useQueryNotificationUserMarkUnread()
    const { mutateAsync: deleteRequest } = useQueryNotificationUserDelete()

    const scopedResourceId = computed(() => toValue(resourceId) ?? null)
    const scopedResourceType = computed(() => toValue(resourceType) ?? null)

    const notifications = computed(() => {
        let list = data.value ?? []

        if (scopedResourceType.value) {
            list = list.filter((notification) => notification.resource_type === scopedResourceType.value)
        }
        if (scopedResourceId.value) {
            list = list.filter((notification) => notification.resource_id === scopedResourceId.value)
        }

        return list
    })

    function notificationsFor(id) {
        const target = toValue(id) ?? null

        return target ? notifications.value.filter((notification) => notification.resource_id === target) : []
    }

    function unreadCountFor(id) {
        return notificationsFor(id).filter((notification) => !notification.read_at).length
    }

    function matchesScope(notification, { id = null, resourceId: scopeResourceId = null } = {}) {
        if (id) return notification.id === id
        if (scopeResourceId) return notification.resource_id === scopeResourceId
        return true
    }

    // A write with no explicit target inherits the instance scope.
    function withScope(scope = {}) {
        if (scope.id || scope.resourceId) return scope

        return scopedResourceId.value ? { resourceId: scopedResourceId.value } : {}
    }

    async function mutate(scope, request, updateList) {
        const target = withScope(scope)
        const previous = queryClient.getQueryData(NOTIFICATION_USER_LIST_QUERY_KEY)

        queryClient.setQueryData(NOTIFICATION_USER_LIST_QUERY_KEY, (list = []) =>
            updateList(list, (notification) => matchesScope(notification, target))
        )

        try {
            await request(target)
        } catch (err) {
            // Put the list back rather than leaving the user looking at a change that never landed.
            queryClient.setQueryData(NOTIFICATION_USER_LIST_QUERY_KEY, previous)
            throw err
        } finally {
            await queryClient.invalidateQueries({ queryKey: NOTIFICATION_USER_LIST_QUERY_KEY })
        }
    }

    function markRead(scope = {}) {
        const readAt = new Date().toISOString()

        return mutate(scope, markReadRequest, (list, matches) =>
            list.map((notification) =>
                matches(notification) && !notification.read_at ? { ...notification, read_at: readAt } : notification
            )
        )
    }

    function markUnread(scope = {}) {
        return mutate(scope, markUnreadRequest, (list, matches) =>
            list.map((notification) =>
                matches(notification) && notification.read_at ? { ...notification, read_at: null } : notification
            )
        )
    }

    function remove(scope = {}) {
        return mutate(scope, deleteRequest, (list, matches) => list.filter((notification) => !matches(notification)))
    }

    return {
        notifications,
        count: computed(() => notifications.value.length),
        unreadCount: computed(() => notifications.value.filter((notification) => !notification.read_at).length),
        readCount: computed(() => notifications.value.filter((notification) => notification.read_at).length),
        isEmpty: computed(() => notifications.value.length === 0),
        notificationsFor,
        unreadCountFor,
        isFetching,
        isError,
        error,
        refresh: refetch,
        markRead,
        markUnread,
        remove,
        routeFor: resolveNotificationResourceRoute,
    }
}
